import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDaily } from '@daily-co/daily-react'
import { noop } from 'lodash'
import { showToast, toast } from '@quickmd-llc/design-system'
import { useDailyVisitInfo } from 'pages/WaitingRoom/daily/hooks/useDailyVisitInfo'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { useDispatch } from 'react-redux'
import { getPatientChart } from 'store/charts/ChartsThunk'
import { getTabId } from 'utils/common'
import { useHandleVisitMqttEvents } from '../hooks/useHandleVisitMqttEvents'
import { useSelector } from 'store/hooks/useSelector'
import {
  putVisitsVisitIdClaim,
  putVisitsVisitIdComplete,
  putVisitsVisitIdEnd,
  putVisitsVisitIdUnclaim,
  putVisitsVisitIdUnresolve,
} from 'api/generated/visit/visit'
import { useGetSettings } from 'api/generated/setting/setting'
import { useGetPatientsPatientId } from 'api/generated/patient/patient'
import {
  getGetVisitsWaitingroomV2QueryKey,
  useGetVisitsVisitIdV2,
} from 'api/generated/visit-controller-v2/visit-controller-v2'
import { getVisitPrescriptionCollection } from 'store/waitingRoom/WaitingRoomThunk'
import { useClearInVisitRoomOnUnmount } from 'hooks/useClearInVisitRoomOnUnmount'
import {
  getGetVisitsVisitIdPrescriptionsQueryKey,
  getVisitsVisitIdPrescriptions,
  useGetVisitsVisitIdPrescriptions,
} from 'api/generated/prescription/prescription'
import { client } from 'api/components/queryClient'
import { useTrackDailySegmentEvents } from '../hooks/useTrackDailySegmentEvents'
import { useVisitCallEventDataRef } from '../hooks/useVisitCallEventDataRef'
import { trackPatientVisitEvent, trackSaveVisitNote } from '../utils'
import { PROVIDER_PATIENT_SOURCE_EVENT_NAMES } from '@quickmd-llc/frontend-utils/events'
import { useGetProfile } from 'api/generated/profile/profile'
import { VisitMethods } from 'constants/VisitMethods'
import moment from 'moment'
import { getDontShowCompleteConfirmation, getDontShowDeferConfirmation } from './utils'
import { clearClaimStart, getOrInitClaimStart, resetClaimStart } from './asyncClaimStartStorage'
import { useOnlineVisitCompletionReminder } from './hooks/useOnlineVisitCompletionReminder'
import { hasSentPrescription } from './components/orders/prescriptionStatus'
import { getErrorObject } from '@quickmd-llc/frontend-utils'
import { useBeforeUnloadWarning } from 'hooks/useBeforeUnloadWarning'
import { useCallInProgress } from 'pages/WaitingRoom/daily/hooks/useCallInProgress'
import type { VisitNoteViewRef } from './components/VisitNoteView'
import type { VisitInstructionsViewRef } from './components/VisitInstructionsView'

type Props = {
  onClose: () => void
  /** Called with the deferred VisitId right after the unclaim resolves, so the dashboard can
   * optimistically flip that row's button to "Start" before the MQTT refresh reconciles. */
  onDeferred?: ((visitId: string) => void) | undefined
  /** Called with the completed VisitId right after the complete resolves, so the dashboard can
   * optimistically drop that row from the online-visits list before the MQTT refresh reconciles. */
  onCompleted?: ((visitId: string) => void) | undefined
}

// Unclaim returns 403 when the visit is no longer assigned to us and 409 when it is no longer in a
// claimed state — both mean it is effectively already released, so Defer should still close cleanly
// (and refresh the queue) rather than surfacing an error.
const isAlreadyReleasedUnclaimError = (e: unknown): boolean =>
  typeof e === 'object' && e !== null && 'status' in e && (e.status === 403 || e.status === 409)

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

// Read AsynchronousVisitsSettings.ClaimTimeoutMinutes defensively (fallback 20). Both fields are
// optional in the contract, and CI regenerates the client against a spec that can lag the backend
// (where getSettings is still typed `void`), so we narrow from `unknown` rather than depend on the
// generated shape — this compiles regardless of which spec version is present.
const getClaimTimeoutMinutes = (settings: unknown): number => {
  if (!isRecord(settings)) return 20
  const asyncSettings = settings.AsynchronousVisitsSettings
  if (!isRecord(asyncSettings)) return 20
  return typeof asyncSettings.ClaimTimeoutMinutes === 'number' ? asyncSettings.ClaimTimeoutMinutes : 20
}

export function useVisitPopUpState({ onClose, onDeferred, onCompleted }: Props) {
  const dispatch = useDispatch()
  const { patientWithVisitInfo, endVisitCall, is, isPrepMode } = useDailyVisit()
  const callInProgress = useCallInProgress()
  const isAsyncVisit = patientWithVisitInfo?.VisitMethod === VisitMethods.ASYNCHRONOUS
  const asyncVisitId = patientWithVisitInfo?.VisitId ?? ''
  const { data: settings } = useGetSettings({ query: { enabled: isAsyncVisit } })
  const claimTimeoutMinutes = getClaimTimeoutMinutes(settings)
  // Anchor the reminder to a persisted claim-start so a refresh keeps the same window. The read
  // lazily initializes on first mount and is idempotent — repeated renders return the same value.
  const claimStartMs = useMemo(
    () => (isAsyncVisit && asyncVisitId ? getOrInitClaimStart(asyncVisitId) : 0),
    [isAsyncVisit, asyncVisitId],
  )
  // handleExpire is defined further down (it needs saveVisitEdits/cleanupCall/onClose); the hook
  // reaches it through a ref so we can wire the reminder before that definition.
  const handleExpireRef = useRef<() => void>(() => {})
  const onReminderExpire = useCallback(() => handleExpireRef.current(), [])
  // Pull the visit's prescriptions fresh and seed the query cache the orders view reads. The
  // prescription queries are disabled (we hydrate them manually), so this is the only path that
  // keeps that cache current. Refreshes come from three uncoordinated sources — mount, the
  // focus/visibilitychange pair (both fire on one tab-return), and each reminder deadline — so go
  // through React Query's fetchQuery: it dedups concurrent calls onto one request (keyed by the
  // query key, which includes the visit, so a mid-flight visit swap can't return the wrong data),
  // `staleTime: 0` keeps every call a real refetch so each deadline sees fresh backend state, and
  // `retry: false` fails fast so an errored check warns promptly instead of waiting on backoff.
  const refreshPrescriptions = useCallback(async () => {
    if (!asyncVisitId) return undefined
    return client.fetchQuery({
      queryKey: getGetVisitsVisitIdPrescriptionsQueryKey(asyncVisitId),
      queryFn: () => getVisitsVisitIdPrescriptions(asyncVisitId),
      staleTime: 0,
      retry: false,
    })
  }, [asyncVisitId])
  // The warning/expiry must not fire once the provider has sent a prescription — the backend
  // keeps the claim in that case. Scripts are written in an external prescribing window (Treat or
  // the DAW/ScriptSure encounter flow) and the cache is seeded once on mount, so re-pull fresh at
  // each deadline rather than trusting stale cache. A failed fetch falls back to "not sent" so the
  // provider is still warned.
  const checkHasSentPrescription = useCallback(async () => {
    try {
      return hasSentPrescription(await refreshPrescriptions())
    } catch (e) {
      console.error('Failed to check prescriptions for inactivity warning', getErrorObject(e))
      return false
    }
  }, [refreshPrescriptions])
  const {
    isStillThereOpen,
    resetWindow: resetReminderWindow,
    stop: stopStillThere,
  } = useOnlineVisitCompletionReminder({
    enabled: isAsyncVisit,
    claimTimeoutMinutes,
    claimStartMs,
    onExpire: onReminderExpire,
    shouldSuppress: checkHasSentPrescription,
  })
  const callObject = useDaily()
  const [isClosePopupOpen, setIsClosePopupOpen] = useState(false)
  const [showCompleteConfirmation, setShowCompleteConfirmation] = useState(false)
  const [showDeferConfirmation, setShowDeferConfirmation] = useState(false)
  const dailyVisitInfo = useDailyVisitInfo()
  const providerProfile = useSelector((state) => state.waitingRoomReducer.providerProfile)
  const [showPatientDisconnected, setShowPatientDisconnected] = useState(false)
  const [, setShowConnecting] = useState(!!dailyVisitInfo)
  const [, setIsCallAnswered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [icdCodesError, setIcdCodesError] = useState<boolean>(false)
  const endVideoId = useRef<string>('')
  const endCallId = useRef<string>('')
  const needToAuthorizePrescriptions = useRef(true)
  const [showAuthorizeOrderReminder, setShowAuthorizeOrderReminder] = useState<any>(null)
  const visitNoteRef = useRef<VisitNoteViewRef>(null)
  const visitInstructionsRef = useRef<VisitInstructionsViewRef>(null)

  const [isUnresolvedOpen, setIsUnresolvedOpen] = useState(false)

  const cleanupCall = useCallback(async () => {
    try {
      callObject?.setLocalVideo(false)
      callObject?.setLocalAudio(false)
    } catch (e) {
      console.error('Failed to stop local media on popup close', e)
    }
    await endVisitCall()
  }, [callObject, endVisitCall])

  // Safety net: ensure media tracks are released if the popup unmounts without going through an explicit exit handler.
  // `leave()` returns a Promise — the sync try/catch cannot catch its rejection, and if the call has already been left
  // via the normal exit path Daily.co's internal generator chain hits a null state and unhandled-rejects.
  useEffect(
    () => () => {
      try {
        callObject?.setLocalVideo(false)
        callObject?.setLocalAudio(false)
        callObject?.leave().catch(noop)
      } catch {
        console.warn(
          'Failed to clean up call on VisitPopUp unmount, this may result in orphaned media tracks if the call is still active',
        )
      }
    },
    [callObject],
  )

  const handleDismissClosePopup = () => {
    setIsClosePopupOpen(false)
    document.getElementById('root')?.removeAttribute('inert')
  }
  const handleConfirmClose = async () => {
    try {
      await cleanupCall()
    } catch (e) {
      console.error('Failed to clean up call on confirm close', e)
    }
    onClose()
  }

  const { data: prescriptions } = useGetVisitsVisitIdPrescriptions(patientWithVisitInfo?.VisitId || '', {
    query: { enabled: false },
  })

  useHandleVisitMqttEvents({
    showPatientDisconnected,
    providerProfile,
    patientWithVisitInfo,
    endVideoId,
    endCallId,
    setShowPatientDisconnected,
    setShowConnecting,
    setIsCallAnswered,
  })

  const isConnecting = is('PROVIDER_CONNECTING')
  useEffect(
    () => () => {
      toast.dismiss('single')
    },
    [],
  )
  useEffect(() => {
    if (isConnecting) {
      setShowPatientDisconnected(false)
      setIsCallAnswered(false)
    }
  }, [isConnecting])

  useEffect(() => {
    if (!showPatientDisconnected) return
    cleanupCall().catch((e) => {
      console.error('Failed to leave call on patient disconnect', e)
    })
  }, [showPatientDisconnected, cleanupCall])

  useEffect(() => {
    if (patientWithVisitInfo?.PatientId) {
      const action = getPatientChart(patientWithVisitInfo.PatientId, getTabId())
      if (action) dispatch(action)
    }
  }, [dispatch, patientWithVisitInfo?.PatientId])

  // Clear `inVisitRoom` on every exit from this pop-up — complete, defer, unresolved,
  // finish-later, plain close, in-person. See the hook for why unmount is the right seam.
  useClearInVisitRoomOnUnmount()

  useEffect(() => {
    refreshPrescriptions().catch((e) => console.error('Failed to load prescriptions', getErrorObject(e)))
  }, [refreshPrescriptions])

  // The prescription queries are disabled, so React Query's own refetch-on-focus never runs. The
  // provider writes scripts in an external prescribing window (Treat or the DAW/ScriptSure encounter
  // flow), so re-pull when they tab back — this keeps the orders list current and lets the inactivity
  // gate see a just-sent script without waiting for a deadline. visibilitychange covers tab switches,
  // focus covers window switches.
  // Scoped to async visits: that's the only method the inactivity gate runs for, and the one-shot
  // mount hydration above still refreshes the orders list for every visit type as before.
  useEffect(() => {
    if (!isAsyncVisit || !asyncVisitId) return undefined
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return
      refreshPrescriptions().catch((e) => console.error('Failed to refresh prescriptions on focus', getErrorObject(e)))
    }
    window.addEventListener('focus', refreshOnReturn)
    document.addEventListener('visibilitychange', refreshOnReturn)
    return () => {
      window.removeEventListener('focus', refreshOnReturn)
      document.removeEventListener('visibilitychange', refreshOnReturn)
    }
  }, [isAsyncVisit, asyncVisitId, refreshPrescriptions])

  const { data: visitDetails } = useGetVisitsVisitIdV2(
    patientWithVisitInfo?.VisitId || '',
    { includeMostRecentMatchingVisit: true },
    {
      query: { enabled: false },
    },
  )
  const { data: patient } = useGetPatientsPatientId(patientWithVisitInfo?.PatientId || '', {
    query: { enabled: false },
  })

  useGetProfile({
    query: { enabled: false },
  })

  const visitForEvents = useMemo(() => {
    const { VisitId, PatientId, VisitName, VisitMethod } = patientWithVisitInfo || {}
    return {
      VisitId: VisitId || '',
      PatientId: PatientId || '',
      ServiceTypeName: visitDetails?.ServiceTypeName ?? '',
      VisitTypeName: VisitName ?? '',
      Method: VisitMethod ?? '',
      DateTime: visitDetails?.FromDateTime ?? '',
    }
  }, [patientWithVisitInfo, visitDetails])
  useTrackDailySegmentEvents(visitForEvents)
  const visitCallEventDataRef = useVisitCallEventDataRef(visitForEvents)

  const inProgressVisitForTracking = useMemo(
    () => ({
      ...visitForEvents,
      Patient: patient ?? { PatientId: patientWithVisitInfo?.PatientId },
    }),
    [visitForEvents, patient, patientWithVisitInfo?.PatientId],
  )

  const friendlyVisitDateTime = useMemo(() => {
    const date = patientWithVisitInfo?.VisitTime
    if (!date) return ''
    const m = moment(date).local()
    if (m.isSame(moment(), 'day')) return `Today, ${m.format('h:mma')}`
    if (m.isSame(moment().add(1, 'day'), 'day')) return `Tomorrow, ${m.format('h:mma')}`
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday, ${m.format('h:mma')}`
    return m.format('MMM D, YYYY, h:mma')
  }, [patientWithVisitInfo?.VisitTime])

  const saveVisitEdits = useCallback(async () => {
    await Promise.all([visitNoteRef.current?.saveNote(), visitInstructionsRef.current?.saveInstructions()])
    trackSaveVisitNote(inProgressVisitForTracking)
  }, [inProgressVisitForTracking])

  const handleClose = useCallback(async () => {
    await saveVisitEdits()
    if (isPrepMode) {
      await cleanupCall()
      onClose()
      return
    }
    setIsClosePopupOpen(true)
  }, [saveVisitEdits, isPrepMode, onClose, cleanupCall])

  const handleSubmitUnresolved = useCallback(
    async (data: { reason: string; otherText?: string }) => {
      trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.VISIT_ENDED, visitCallEventDataRef.current)
      try {
        await putVisitsVisitIdEnd(patientWithVisitInfo?.VisitId || '')
        await putVisitsVisitIdUnresolve(patientWithVisitInfo?.VisitId || '', {
          Message: (data.reason === 'other' ? data.otherText : data.reason) ?? '',
        })
        client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
        setIsUnresolvedOpen(false)
        stopStillThere()
        clearClaimStart(patientWithVisitInfo?.VisitId || '')
        await cleanupCall()
        onClose()
      } catch {
        showToast({ type: 'error', message: 'Failed to mark visit as unresolved.' })
      }
    },
    [onClose, patientWithVisitInfo?.VisitId, visitCallEventDataRef, cleanupCall, stopStillThere],
  )

  const handleComplete = useCallback(async () => {
    setShowCompleteConfirmation(false)
    stopStillThere()

    trackPatientVisitEvent(PROVIDER_PATIENT_SOURCE_EVENT_NAMES.VISIT_ENDED, visitCallEventDataRef.current)
    try {
      const completedVisitId = patientWithVisitInfo?.VisitId || ''
      await putVisitsVisitIdEnd(completedVisitId)
      await putVisitsVisitIdComplete(completedVisitId)
      // Optimistically drop this visit from the dashboard's online list now (the backend is
      // eventually consistent, so the immediate refetch may still report it). The MQTT-driven
      // refresh later reconciles this.
      if (completedVisitId) {
        onCompleted?.(completedVisitId)
        clearClaimStart(completedVisitId)
      }
      client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
      await cleanupCall()
      onClose()
    } catch {
      showToast({ type: 'error', message: 'Failed to complete visit.' })
    }
  }, [onClose, patientWithVisitInfo?.VisitId, visitCallEventDataRef, cleanupCall, stopStillThere, onCompleted])

  const handleDefer = useCallback(async () => {
    try {
      await saveVisitEdits()
      const deferredVisitId = patientWithVisitInfo?.VisitId || ''
      try {
        await putVisitsVisitIdUnclaim(deferredVisitId)
      } catch (e) {
        // Already released (403/404-style) is fine — proceed to close and refresh the queue.
        if (!isAlreadyReleasedUnclaimError(e)) throw e
      }
      // Optimistically flip this visit's dashboard row back to "Start" now (the backend is
      // eventually consistent, so the immediate refetch may still report it 'pending'). The
      // MQTT-driven refresh later reconciles this.
      if (deferredVisitId) {
        onDeferred?.(deferredVisitId)
        clearClaimStart(deferredVisitId)
      }
      client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
      stopStillThere()
      await cleanupCall()
      onClose()
    } catch {
      showToast({ type: 'error', message: 'Failed to defer visit.' })
    }
  }, [saveVisitEdits, patientWithVisitInfo?.VisitId, stopStillThere, cleanupCall, onClose, onDeferred])

  // Claim-timeout expiry: the provider didn't respond to the reminder within the final minute. Save
  // their in-progress note (best-effort) and close. We do NOT unclaim — the backend auto-releases at
  // the timeout and emits `asyncvisitreleased` over MQTT — but we optimistically flip the dashboard
  // row back to "Start" so the visit is immediately claimable again.
  const handleExpire = useCallback(async () => {
    const expiredVisitId = patientWithVisitInfo?.VisitId || ''
    try {
      await saveVisitEdits()
    } catch (e) {
      console.error('Failed to save edits on claim-timeout expiry', e)
    }
    if (expiredVisitId) {
      clearClaimStart(expiredVisitId)
      onDeferred?.(expiredVisitId)
    }
    client.invalidateQueries({ queryKey: getGetVisitsWaitingroomV2QueryKey() })
    await cleanupCall()
    onClose()
  }, [patientWithVisitInfo?.VisitId, saveVisitEdits, onDeferred, cleanupCall, onClose])

  useEffect(() => {
    handleExpireRef.current = handleExpire
  }, [handleExpire])

  // "Continue visit" from the reminder: the provider is still here, so reset the claim window. There
  // is no extend endpoint, so we release then immediately re-claim (a small race — another provider
  // could grab it in the gap). On success we re-anchor and keep the pop-up open; on failure we toast
  // and close, and the provider can re-claim from the dashboard.
  const handleStillThereContinue = useCallback(async () => {
    const visitId = patientWithVisitInfo?.VisitId || ''
    if (!visitId) return
    try {
      try {
        await putVisitsVisitIdUnclaim(visitId)
      } catch (e) {
        if (!isAlreadyReleasedUnclaimError(e)) throw e
      }
      await putVisitsVisitIdClaim(visitId)
      resetReminderWindow(resetClaimStart(visitId))
    } catch {
      showToast({ type: 'error', message: 'This visit was picked up by another provider.' })
      clearClaimStart(visitId)
      stopStillThere()
      await cleanupCall()
      onClose()
    }
  }, [patientWithVisitInfo?.VisitId, resetReminderWindow, stopStillThere, cleanupCall, onClose])

  // Defer BUTTON handler: confirm first (unless the provider opted out). The browser Back path
  // below intentionally skips this and defers directly.
  const requestDefer = useCallback(() => {
    if (getDontShowDeferConfirmation()) {
      handleDefer()
      return
    }
    setShowDeferConfirmation(true)
  }, [handleDefer])

  // Warn before the tab closes/reloads, but only while a call is live or being placed — see
  // `useCallInProgress` for exactly which lifecycle states count and why (QMD-4225).
  useBeforeUnloadWarning(callInProgress)

  // Treat the browser Back button on an online visit like the Defer button: cancel the navigation
  // and defer (save + unclaim + close). The pop-up then closes, so a second Back press leaves the
  // page normally. Scoped popstate listener (not window.onpopstate) so it doesn't clobber the
  // App/WaitingRoom global handlers. Mirrors the proven VisitCallRoom back-interception pattern.
  const handleDeferRef = useRef(handleDefer)
  useEffect(() => {
    handleDeferRef.current = handleDefer
  }, [handleDefer])
  useEffect(() => {
    if (!isAsyncVisit) return undefined
    window.history.pushState(null, '', window.location.href)
    let deferred = false
    const onPopState = () => {
      // Re-pin so the browser stays on the page while we defer asynchronously.
      window.history.pushState(null, '', window.location.href)
      if (deferred) return
      deferred = true
      handleDeferRef.current()
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [isAsyncVisit])

  const checkCanCompleteVisit = useCallback(async () => {
    if (needToAuthorizePrescriptions.current && prescriptions?.length) {
      const hasUnauthorizedOrder = prescriptions.find(
        (order: any) => order.Status === 'pendingauthorization' && order.EncounterUrl,
      )
      if (hasUnauthorizedOrder) {
        setShowAuthorizeOrderReminder(hasUnauthorizedOrder)
        needToAuthorizePrescriptions.current = false
        return
      }
    }

    await saveVisitEdits()
    if (visitDetails?.IcdCodes?.length || visitDetails?.Method === VisitMethods.INPERSON) {
      if (getDontShowCompleteConfirmation()) {
        await handleComplete()
      } else {
        setShowCompleteConfirmation(true)
      }
    } else {
      showToast({
        type: 'error',
        message: 'Please add at least one ICD-10 code before completing the visit.',
      })
      setIcdCodesError(true)
    }
  }, [prescriptions, saveVisitEdits, visitDetails?.IcdCodes?.length, visitDetails?.Method, handleComplete])

  const handleUnresolved = useCallback(async () => {
    await saveVisitEdits()
    setIsUnresolvedOpen(true)
  }, [saveVisitEdits])

  const handleDismissUnresolved = useCallback(() => {
    setIsUnresolvedOpen(false)
  }, [])

  const getOrders = useCallback(() => {
    if (patientWithVisitInfo?.VisitId) {
      dispatch(getVisitPrescriptionCollection(patientWithVisitInfo.VisitId, true))
    }
  }, [dispatch, patientWithVisitInfo?.VisitId])

  return {
    patientWithVisitInfo,
    isPrepMode,
    patient,
    visitDetails,
    friendlyVisitDateTime,
    visitNoteRef,
    visitInstructionsRef,

    isClosePopupOpen,
    showCompleteConfirmation,
    setShowCompleteConfirmation,
    showDeferConfirmation,
    setShowDeferConfirmation,
    requestDefer,
    isUnresolvedOpen,
    showAuthorizeOrderReminder,
    setShowAuthorizeOrderReminder,
    isLoading,
    setIsLoading,
    icdCodesError,
    setIcdCodesError,
    handleClose,
    handleDefer,
    handleDismissClosePopup,
    handleConfirmClose,
    handleSubmitUnresolved,
    handleComplete,
    checkCanCompleteVisit,
    handleUnresolved,
    handleDismissUnresolved,
    getOrders,
    isStillThereOpen,
    handleStillThereContinue,
    claimTimeoutMinutes,
  }
}
